import {
  enhancedApi as generatedBillingApi,
  type GetV2ProviderBillingEncountersUnifiedWorklistApiArg,
  type GetV2ProviderBillingEncountersUnifiedWorklistApiResponse,
  type UnifiedEncounterWorklistItem as GeneratedUnifiedEncounterWorklistItem,
} from '@store/generated/billing';
import type {
  BatchSubmitClaimsRequest,
  BatchSubmitClaimsResponse,
  ClaimPdfGenerateResponse,
  SubmitClaimRequest,
  SubmitClaimResponse,
} from '@store/generated/claims';
import { enhancedApi as claimsApi } from '@store/generated/claims';

export type { ClaimAttentionRequest, ClaimAttentionResponse } from '@store/generated/claims';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Extract filename from Content-Disposition header.
 * Handles both `filename="..."` and `filename=...` formats.
 */
function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const match = header.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
  return match ? match[1].replace(/['"]/g, '').trim() : null;
}

/**
 * Shared responseHandler for X12 file download endpoints.
 * Returns blob + parsed filename from Content-Disposition header.
 */
async function x12DownloadResponseHandler(
  response: Response,
  fallbackFilename: string,
): Promise<{ blob: Blob; filename: string }> {
  if (!response.ok) {
    const error = (await response.json()) as { message?: string };
    throw new Error(error?.message || 'Failed to download file');
  }
  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const filename = parseContentDispositionFilename(disposition) || fallbackFilename;
  return { blob, filename };
}

function compactParams<T extends Record<string, unknown>>(params: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  ) as Partial<T>;
}

export type UnifiedEncounterWorkState = NonNullable<
  GetV2ProviderBillingEncountersUnifiedWorklistApiArg['workState']
>;
export type UnifiedEncounterStage = NonNullable<
  GetV2ProviderBillingEncountersUnifiedWorklistApiArg['stage']
>;
export type UnifiedEncounterSeverity = NonNullable<
  GetV2ProviderBillingEncountersUnifiedWorklistApiArg['severity']
>;
/**
 * The concrete next step a worklist row is waiting on (GDN-2769/GDN-2770).
 * Re-exported from the generated request arg so the vocabulary stays in sync
 * with the backend — never hand-write or widen this union.
 */
export type UnifiedEncounterNextStep = NonNullable<
  GetV2ProviderBillingEncountersUnifiedWorklistApiArg['nextStep']
>;
/**
 * Active submit-stage claim problem selected by the worklist filter (GDN-2859).
 *
 * TEMPORARY literal union: the api-test spec has not yet published the
 * `claim_problem` query param, so the generated Arg type has no
 * `claimProblem` member and deriving from it breaks `tsc` repo-wide. The
 * values mirror `claimProblemLabels` in UnifiedEncounterWorklistTab. Once
 * codegen picks the param up, re-derive as
 * `NonNullable<GetV2ProviderBillingEncountersUnifiedWorklistApiArg['claimProblem']>`.
 */
export type UnifiedEncounterClaimProblem =
  | 'any_problem'
  | 'rejected_or_denied'
  | 'rejected'
  | 'denied'
  | 'scrub_failed'
  | 'reversal_or_recoupment'
  | 'unmatched_remittance';
export type UnifiedEncounterWorklistItem = GeneratedUnifiedEncounterWorklistItem;
export type UnifiedEncounterWorklistResponse =
  GetV2ProviderBillingEncountersUnifiedWorklistApiResponse;
export type UnifiedEncounterWorklistSegment =
  UnifiedEncounterWorklistResponse['segments'][number];

export type UnifiedEncounterWorklistQueryArgs = Omit<
  GetV2ProviderBillingEncountersUnifiedWorklistApiArg,
  'owner' | 'providerIds'
> & {
  owner?: string;
  providerIds?: string[];
  billWithJotpsych?: boolean;
  // TEMPORARY: remove once the generated Arg carries `claimProblem` (see
  // UnifiedEncounterClaimProblem above).
  claimProblem?: UnifiedEncounterClaimProblem;
};

export interface PayerExpectedRateResponse {
  id: string;
  payer_id: string;
  code_id: string;
  cpt_code: string;
  expected_allowed_cents: number;
  effective_date: string;
}

export interface PayerExpectedRatesGetResponse {
  organization_id: string;
  payer_id: string;
  rates: PayerExpectedRateResponse[];
  count: number;
}

export interface PayerExpectedRatesUpsertRequest {
  rates: Array<{
    code_id?: string;
    cpt_code?: string;
    expected_allowed_cents: number;
  }>;
  effective_date?: string;
}

export interface PayerExpectedRatesUpsertResponse {
  organization_id: string;
  payer_id: string;
  rates: Array<PayerExpectedRateResponse & { created: boolean }>;
  created_count: number;
  updated_count: number;
}

export interface PayerExpectedRateDeleteResponse {
  payer_id: string;
  code_id: string;
  deleted: boolean;
}

const patientResponsibilitySettlementTags = (patientId: string, encounterId?: string | null) => [
  { type: 'PatientBalance' as const, id: patientId },
  { type: 'PatientInvoices' as const, id: patientId },
  { type: 'BillingV2PatientDetail' as const, id: patientId },
  { type: 'BillingV2PatientBilling' as const },
  { type: 'BillingV2Overview' as const },
  { type: 'BillingEncountersList' as const, id: 'UNIFIED_WORKLIST' },
  { type: 'BillingEncounter' as const },
  ...(encounterId
    ? [
        {
          type: 'BillingEncounter' as const,
          id: encounterId,
        },
      ]
    : []),
  { type: 'Eligibility' as const },
  { type: 'Worklist' as const },
  { type: 'PatientPageV3Bootstrap' as const, id: patientId },
];

/**
 * Feature-specific tag types for cache management
 */
const BILLING_TAG_TYPES = [
  'Claim',
  'ClaimsList',
  'BillingClaimsList',
  'ClaimQueueCounts',
  'BillingEncounter',
  'BillingEncountersList',
  'ClaimPdf',
  'FeeSchedule',
  // Cross-feature: invalidate encounter cache when claims change
  'Encounter',
  // V2 cross-invalidation (tags registered by billingV2Api, shared via baseApi)
  'BillingV2Overview',
  'BillingV2Work',
  'BillingV2Claims',
  'BillingV2ClaimWork',
  'BillingV2ClaimCases',
  'BillingV2PatientBilling',
] as const;

/**
 * Claims API - extends the generated claims OpenAPI client
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization
 */
const extendedClaimsApi = claimsApi
  .enhanceEndpoints({
    addTagTypes: BILLING_TAG_TYPES,
    endpoints: {
      // Enhance generated endpoints with cache tags
      getV2ProviderClaims: {
        providesTags: result =>
          result?.claims
            ? [
                ...result.claims.map(claim => ({
                  type: 'Claim' as const,
                  id: claim.id ?? 'unknown',
                })),
                { type: 'ClaimsList' as const, id: 'LIST' },
              ]
            : [{ type: 'ClaimsList' as const, id: 'LIST' }],
      },

      postV2ProviderClaims: {
        invalidatesTags: [
          { type: 'ClaimsList', id: 'LIST' },
          { type: 'BillingClaimsList', id: 'LIST' },
          { type: 'ClaimQueueCounts', id: 'LIST' },
          { type: 'BillingEncountersList', id: 'LIST' },
          { type: 'Encounter' },
          { type: 'BillingV2ClaimWork' },
        ],
      },

      getV2ProviderClaimsByClaimId: {
        providesTags: (_result, _error, arg) => [{ type: 'Claim', id: arg.claimId }],
      },

      putV2ProviderClaimsByClaimId: {
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Claim', id: arg.claimId },
          { type: 'ClaimsList', id: 'LIST' },
          { type: 'BillingClaimsList', id: 'LIST' },
          { type: 'ClaimQueueCounts', id: 'LIST' },
          { type: 'BillingV2ClaimWork' },
        ],
      },

      deleteV2ProviderClaimsByClaimId: {
        // Deliberately omits { type: 'Claim', id: arg.claimId }. Both by-id
        // detail queries provide that tag, and the claim detail route is still
        // mounted when the mutation fulfills — RTK Query dispatches
        // invalidation inside .unwrap(), before the caller can navigate away.
        // Invalidating the deleted claim therefore refetched a row that no
        // longer exists, 404ing on both endpoints for every deletion
        // (gdn-2700; same race CUS-772 fixed on the calendar). The LIST tags
        // below are what actually drop the row from the claims surfaces.
        invalidatesTags: () => [
          { type: 'BillingEncounter' },
          { type: 'ClaimsList', id: 'LIST' },
          { type: 'BillingClaimsList', id: 'LIST' },
          { type: 'ClaimQueueCounts', id: 'LIST' },
          { type: 'BillingV2ClaimWork' },
        ],
      },

      getV2ProviderClaimsByClaimIdPdf: {
        providesTags: (_result, _error, arg) => [{ type: 'ClaimPdf', id: arg.claimId }],
      },

      postV2ProviderClaimsByClaimIdScrub: {
        invalidatesTags: (result, _error, arg) =>
          result
            ? [
                { type: 'Claim', id: arg.claimId },
                { type: 'BillingEncounter' },
                { type: 'BillingV2ClaimWork' },
              ]
            : [],
        // Update the claim in cache with scrub results
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            // Update the specific claim query cache with the scrub results
            dispatch(
              billingApi.util.updateQueryData('getV2ProviderClaimsByClaimId', 
                { claimId: arg.claimId }, 
                () => {
                  // The scrub response is the updated claim
                  return data;
                }
              )
            );
          } catch {
            // Error handled by mutation hook
          }
        },
      },

      postV2ProviderClaimsByClaimIdRebuild: {
        // Invalidate claim and list since rebuild regenerates the claim
        invalidatesTags: (result, error, arg) =>
          result ? [
            { type: 'Claim', id: arg.claimId },
            { type: 'ClaimsList', id: 'LIST' },
            { type: 'BillingClaimsList', id: 'LIST' },
            { type: 'ClaimQueueCounts', id: 'LIST' },
            { type: 'BillingEncounter' },
            { type: 'BillingV2ClaimWork' },
          ] : [],
      },

      postV2ProviderClaimsByClaimIdResubmit: {
        // Invalidate original claim (has_children changes) + all lists + queue counts
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Claim', id: arg.claimId },
          { type: 'ClaimsList', id: 'LIST' },
          { type: 'BillingClaimsList', id: 'LIST' },
          { type: 'ClaimQueueCounts', id: 'LIST' },
          { type: 'BillingV2ClaimWork' },
        ],
      },

      postV2ProviderClaimsByClaimIdAttention: {
        // Dismiss/snooze changes attention_disposition — refresh claim + v2 lists.
        // BillingV2ClaimCases is the review-queue tag; without it a dismissed/
        // snoozed claim lingers in the Review sub-tab until a manual refresh.
        // BillingEncounter(sList) cover disposition triggered from the unified
        // encounter worklist and the encounter-detail claim sub-tab. The mutation
        // arg is a claimId, not an encounterId, so the open encounter detail is
        // refreshed by invalidating the BillingEncounter type broadly.
        invalidatesTags: (_result, _error, arg) => [
          { type: 'Claim', id: arg.claimId },
          { type: 'BillingClaimsList', id: 'LIST' },
          { type: 'BillingEncounter' as const },
          { type: 'BillingEncountersList', id: 'UNIFIED_WORKLIST' },
          { type: 'BillingV2Claims' as const },
          { type: 'BillingV2ClaimWork' as const },
          { type: 'BillingV2ClaimCases' as const },
          { type: 'BillingV2Work' as const },
          { type: 'BillingV2Overview' as const },
        ],
      },

      postV2ProviderClaimsByClaimIdSubmit: {
        // Invalidate claim and list since submit changes claim status
        invalidatesTags: (result, error, arg) =>
          result ? [
            { type: 'Claim', id: arg.claimId },
            { type: 'ClaimsList', id: 'LIST' },
            { type: 'BillingClaimsList', id: 'LIST' },
            { type: 'ClaimQueueCounts', id: 'LIST' },
            // V2 cross-invalidation
            { type: 'BillingV2Overview' as const },
            { type: 'BillingV2Work' as const },
            { type: 'BillingV2Claims' as const },
            { type: 'BillingV2ClaimWork' as const },
          ] : [],
        // Update the claim in cache with submit results
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            // Update the specific claim query cache with the submit results
            if (data?.claim) {
              dispatch(
                billingApi.util.updateQueryData('getV2ProviderClaimsByClaimId', 
                  { claimId: arg.claimId }, 
                  () => {
                    // The submit response contains the updated claim
                    return data.claim;
                  }
                )
              );
            }
          } catch {
            // Error handled by mutation hook
          }
        },
      },
    },
  });

/**
 * Fee Schedule API - extends the generated billing OpenAPI client
 * Handles baseline CPT rates for the organization
 */
const extendedBillingApi = generatedBillingApi
  .enhanceEndpoints({
    addTagTypes: ['FeeSchedule', 'BillingEncounter', 'BillingEncountersList', 'BillingClaimsList', 'ClaimQueueCounts', 'Claim', 'RemittancesList', 'RemittanceQueueCounts', 'BillingInvoices', 'PatientInvoices', 'PatientBalance', 'StatementsQueue', 'StatementsHistory', 'BillingV2Overview', 'BillingV2Work', 'BillingV2Claims', 'BillingV2ClaimWork', 'BillingV2PatientBilling', 'BillingV2PatientDetail', 'Eligibility', 'Worklist', 'PatientPageV3Bootstrap'],
    endpoints: {
      // Org-scoped claims list (new enriched endpoint)
      getV2ProviderBillingClaims: {
        providesTags: result =>
          result?.claims
            ? [
                ...result.claims.map(claim => ({
                  type: 'Claim' as const,
                  id: claim.claim_id,
                })),
                { type: 'BillingClaimsList' as const, id: 'LIST' },
              ]
            : [{ type: 'BillingClaimsList' as const, id: 'LIST' }],
      },

      // Queue badge counts
      getV2ProviderBillingClaimsQueueCounts: {
        providesTags: [{ type: 'ClaimQueueCounts' as const, id: 'LIST' }],
      },

      // Org-scoped claim detail
      getV2ProviderBillingClaimsByClaimId: {
        providesTags: (_result, _error, arg) => [{ type: 'Claim' as const, id: arg.claimId }],
      },

      // Encounters list
      getV2ProviderBillingEncounters: {
        providesTags: result =>
          result?.encounters
            ? [
                ...result.encounters.map(({ encounter_id }) => ({
                  type: 'BillingEncounter' as const,
                  id: encounter_id,
                })),
                { type: 'BillingEncountersList' as const, id: 'LIST' },
              ]
            : [{ type: 'BillingEncountersList' as const, id: 'LIST' }],
      },

      // Single encounter detail
      getV2ProviderBillingEncountersByEncounterId: {
        providesTags: (_result, _error, arg) => [
          { type: 'BillingEncounter' as const, id: arg.encounterId },
        ],
      },

      // Single encounter 5S flow
      getV2ProviderBillingEncountersByEncounterIdFlow: {
        providesTags: (_result, _error, arg) => [
          { type: 'BillingEncounter' as const, id: arg.encounterId },
        ],
      },

      // Unified encounter billing read model
      getV2ProviderBillingEncountersByEncounterIdUnified: {
        providesTags: (_result, _error, arg) => [
          { type: 'BillingEncounter' as const, id: arg.encounterId },
        ],
      },

      getV2ProviderBillingEncountersByEncounterIdClaimWorkspace: {
        providesTags: (_result, _error, arg) => [
          { type: 'BillingEncounter' as const, id: arg.encounterId },
        ],
      },

      postV2ProviderBillingEncountersByEncounterIdClaimWorkspaceRefresh: {
        invalidatesTags: (result, _error, arg) =>
          result
            ? [
                { type: 'BillingEncounter' as const, id: arg.encounterId },
                { type: 'BillingEncountersList' as const, id: 'LIST' },
                { type: 'BillingEncountersList' as const, id: 'UNIFIED_WORKLIST' },
                { type: 'BillingV2ClaimWork' as const },
              ]
            : [],
        /**
         * The response IS the fresh workspace, so it is written straight into
         * the read cache rather than invalidating into a second GET.
         *
         * BOTH cache keys, because the workspace query is read two ways: the
         * encounter surfaces subscribe with no claim (`{encounterId}`), and the
         * claim workbench pins the claim it was opened on
         * (`{encounterId, claimId}` — hooks/useClaimWorkspaceModel.ts). Since
         * GDN-3090 step 2 every mirrored claim edit IS this call, and the
         * workbench clears its optimistic overlay the moment it resolves — so
         * missing the pinned key would drop the biller's just-saved value into
         * a cache entry that never received it.
         */
        async onQueryStarted(arg, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            const write = (claimId?: string) =>
              dispatch(
                extendedBillingApi.util.updateQueryData(
                  'getV2ProviderBillingEncountersByEncounterIdClaimWorkspace',
                  { encounterId: arg.encounterId, ...(claimId ? { claimId } : {}) },
                  () => data,
                ),
              );
            write();
            const pinnedClaimId = arg.unifiedEncounterClaimWorkspaceRefreshRequest?.claim_id;
            if (pinnedClaimId) write(pinnedClaimId);
          } catch {
            // Error handled by mutation hook.
          }
        },
      },

      // GET baseline rates
      getV2ProviderBillingFeeSchedulesBaseline: {
        providesTags: [{ type: 'FeeSchedule' as const, id: 'LIST' }],
      },

      // PUT baseline rates (bulk upsert)
      putV2ProviderBillingFeeSchedulesBaseline: {
        invalidatesTags: [{ type: 'FeeSchedule' as const, id: 'LIST' }],
      },

      // DELETE single baseline rate by code ID
      deleteV2ProviderBillingFeeSchedulesBaselineByCodeId: {
        invalidatesTags: [{ type: 'FeeSchedule' as const, id: 'LIST' }],
      },

      // Import fee schedule from extraction result
      postV2ProviderBillingFeeSchedulesFromExtraction: {
        invalidatesTags: [{ type: 'FeeSchedule' as const, id: 'LIST' }],
      },

      // Remittances list
      getV2ProviderBillingRemittances: {
        providesTags: result =>
          result?.remittances
            ? [
                ...result.remittances.map(r => ({
                  type: 'RemittancesList' as const,
                  id: r.remittance_id,
                })),
                { type: 'RemittancesList' as const, id: 'LIST' },
              ]
            : [{ type: 'RemittancesList' as const, id: 'LIST' }],
      },

      // Remittance queue badge counts
      getV2ProviderBillingRemittancesQueueCounts: {
        providesTags: [{ type: 'RemittanceQueueCounts' as const, id: 'LIST' }],
      },

      // Patient balance
      getV2ProviderBillingPatientsByPatientIdBalance: {
        providesTags: (_result, _error, { patientId }) => [
          { type: 'PatientBalance' as const, id: patientId },
        ],
      },

      // Record manual payment (and the create_charge=true flavor used by
      // the eligibility worklist's Log-collection form) → refresh
      // balance + encounter billing. Eligibility readiness reads
      // patient_due_status off the billing summary to flip between
      // collect and collected, so we invalidate Eligibility + Worklist
      // alongside the billing tags. Slight over-invalidation for plain
      // payment records — cost is one stale-eligibility refetch, harmless.
      postV2ProviderBillingPatientsByPatientIdPayments: {
        invalidatesTags: (_result, _error, { patientId, recordPaymentRequest }) => [
          ...patientResponsibilitySettlementTags(
            patientId,
            recordPaymentRequest.encounter_id,
          ),
          { type: 'BillingEncountersList' as const, id: 'LIST' },
        ],
      },

      // Capture self-pay charges → refresh balance + encounter billing +
      // encounters list + the patient financial summary (Patient Ledger
      // Drawer reads off the latter).
      postV2ProviderBillingCharges: {
        invalidatesTags: (_result, _error, { captureChargesRequest }) => [
          { type: 'PatientBalance' as const },
          { type: 'BillingEncounter' as const },
          { type: 'BillingEncountersList' as const, id: 'LIST' },
          // V2 cross-invalidation
          { type: 'BillingV2Overview' as const },
          { type: 'BillingV2PatientBilling' as const },
          {
            type: 'BillingV2PatientDetail' as const,
            id: captureChargesRequest.patient_id,
          },
        ],
      },


      // Reverse a manually-created charge line (gdn-2915) → the request only
      // carries the charge_line_id, so invalidate the patient billing
      // surfaces broadly (same shape as the reminder endpoint's id-less
      // invalidation). One over-broad refetch beats a stale balance.
      postV2ProviderBillingChargeLinesByChargeLineIdReverse: {
        invalidatesTags: () => [
          { type: 'PatientBalance' as const },
          { type: 'PatientInvoices' as const },
          { type: 'BillingV2PatientDetail' as const },
          { type: 'BillingV2PatientBilling' as const },
          { type: 'BillingV2Overview' as const },
          { type: 'BillingEncounter' as const },
          { type: 'BillingEncountersList' as const, id: 'LIST' },
          { type: 'Eligibility' as const },
          { type: 'Worklist' as const },
        ],
      },

      // Write-off → refresh the same patient billing surfaces as invoice send:
      // ledger/financial summary, invoice list, patient billing queue, and
      // worklist/eligibility rows that depend on patient_due_status.
      postV2ProviderBillingPatientsByPatientIdWriteOffs: {
        invalidatesTags: (_result, _error, { patientId, writeOffRequest }) =>
          patientResponsibilitySettlementTags(patientId, writeOffRequest.encounter_id),
      },

      // Statements queue
      getV2ProviderBillingStatementsQueue: {
        providesTags: [{ type: 'StatementsQueue' as const, id: 'LIST' }],
      },

      // Statements history
      getV2ProviderBillingStatementsHistory: {
        providesTags: [{ type: 'StatementsHistory' as const, id: 'LIST' }],
      },

      // Org-wide invoice list (local DB)
      getV2ProviderBillingInvoices: {
        providesTags: result =>
          result?.invoices
            ? [
                ...result.invoices.map(invoice => ({
                  type: 'BillingInvoices' as const,
                  id: invoice.id,
                })),
                { type: 'BillingInvoices' as const, id: 'LIST' },
              ]
            : [{ type: 'BillingInvoices' as const, id: 'LIST' }],
      },
    },
  })
  .injectEndpoints({
    endpoints: build => ({
      // Single ERA (835) download
      downloadRemittanceEra: build.query<
        { blob: Blob; filename: string },
        { remittanceId: string }
      >({
        query: ({ remittanceId }) => ({
          url: `/v2/provider/billing/remittances/${remittanceId}/download`,
          responseHandler: (response: Response) =>
            x12DownloadResponseHandler(response, `ERA_${remittanceId}.835`),
        }),
        keepUnusedDataFor: 0,
      }),

      // Bulk ERA (835) download — returns ZIP
      downloadRemittanceErasBulk: build.mutation<
        { blob: Blob; filename: string },
        { remittanceIds: string[] }
      >({
        query: ({ remittanceIds }) => ({
          url: '/v2/provider/billing/remittances/download',
          method: 'POST',
          body: { remittance_ids: remittanceIds },
          responseHandler: (response: Response) => {
            const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
            return x12DownloadResponseHandler(response, `ERA_${today}.zip`);
          },
        }),
      }),

      getUnifiedEncounterWorklist: build.query<
        UnifiedEncounterWorklistResponse,
        UnifiedEncounterWorklistQueryArgs | undefined
      >({
        query: args => ({
          url: '/v2/provider/billing/encounters/unified-worklist',
          params: compactParams({
            work_state: args?.workState,
            stage: args?.stage,
            patient_id: args?.patientId,
            provider_ids: args?.providerIds?.join(','),
            payer_uuid: args?.payerUuid,
            date_from: args?.dateFrom,
            date_to: args?.dateTo,
            owner: args?.owner,
            severity: args?.severity,
            next_step: args?.nextStep,
            claim_problem: args?.claimProblem,
            has_claim: args?.hasClaim,
            stale_only: args?.staleOnly,
            bill_with_jotpsych: args?.billWithJotpsych,
            sort_by: args?.sortBy,
            sort_order: args?.sortOrder,
            page: args?.page,
            per_page: args?.perPage,
          }),
        }),
        providesTags: result =>
          result?.items
            ? [
                ...result.items.map(item => ({
                  type: 'BillingEncounter' as const,
                  id: item.encounter_id,
                })),
                { type: 'BillingEncountersList' as const, id: 'UNIFIED_WORKLIST' },
              ]
            : [{ type: 'BillingEncountersList' as const, id: 'UNIFIED_WORKLIST' }],
      }),

      getPayerExpectedRates: build.query<
        PayerExpectedRatesGetResponse,
        { payerId: string; effectiveDate?: string }
      >({
        query: ({ payerId, effectiveDate }) => ({
          url: `/v2/provider/billing/fee-schedules/payers/${payerId}/expected-rates`,
          params: compactParams({ effective_date: effectiveDate }),
        }),
        providesTags: (_result, _error, { payerId }) => [
          { type: 'FeeSchedule' as const, id: `payer-${payerId}` },
        ],
      }),

      updatePayerExpectedRates: build.mutation<
        PayerExpectedRatesUpsertResponse,
        { payerId: string; payerExpectedRatesUpsertRequest: PayerExpectedRatesUpsertRequest }
      >({
        query: ({ payerId, payerExpectedRatesUpsertRequest }) => ({
          url: `/v2/provider/billing/fee-schedules/payers/${payerId}/expected-rates`,
          method: 'PUT',
          body: payerExpectedRatesUpsertRequest,
        }),
        invalidatesTags: (_result, _error, { payerId }) => [
          { type: 'FeeSchedule' as const, id: `payer-${payerId}` },
          { type: 'BillingEncountersList' as const, id: 'UNIFIED_WORKLIST' },
          { type: 'BillingEncounter' as const },
        ],
      }),

      deletePayerExpectedRate: build.mutation<
        PayerExpectedRateDeleteResponse,
        { payerId: string; codeId: string }
      >({
        query: ({ payerId, codeId }) => ({
          url: `/v2/provider/billing/fee-schedules/payers/${payerId}/expected-rates/${encodeURIComponent(codeId)}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, _error, { payerId }) => [
          { type: 'FeeSchedule' as const, id: `payer-${payerId}` },
          { type: 'BillingEncountersList' as const, id: 'UNIFIED_WORKLIST' },
          { type: 'BillingEncounter' as const },
        ],
      }),
    }),
  });

/**
 * Manually injected endpoints not yet in the OpenAPI spec
 */
const injectedClaimsApi = extendedClaimsApi.injectEndpoints({
  endpoints: build => ({
    getStediPdf: build.query<
      ClaimPdfGenerateResponse,
      { claimId: string; background?: boolean }
    >({
      query: ({ claimId, background = true }) => ({
        url: `/v2/provider/claims/${claimId}/stedi-pdf`,
        params: { background },
      }),
      providesTags: (_result, _error, arg) => [{ type: 'ClaimPdf' as const, id: `stedi-${arg.claimId}` }],
    }),
    batchSubmitClaims: build.mutation<
      BatchSubmitClaimsResponse,
      {
        batchSubmitClaimsRequest: BatchSubmitClaimsRequest;
        /**
         * Optional `Idempotency-Key` header. The server replays the cached
         * response for duplicate keys, so a double-clicked "Submit All"
         * button can't enqueue the same batch twice (gdn-1051). See
         * `useBatchClaimSubmit` for the caller-facing wrapper.
         */
        idempotencyKey?: string;
      }
    >({
      query: ({ batchSubmitClaimsRequest, idempotencyKey }) => ({
        url: '/v2/provider/claims/batch-submit',
        method: 'POST',
        body: batchSubmitClaimsRequest,
        headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : undefined,
      }),
    }),

    // Same wire format as the generated postV2ProviderClaimsByClaimIdSubmit
    // mutation, but accepts an idempotency key that travels as a header. Paired
    // with the backend Idempotency-Key support — a repeat of the same submit
    // (e.g. from a double-click before React's isSubmitting state lands)
    // replays the first response instead of double-submitting to the
    // clearinghouse.
    submitClaimIdempotent: build.mutation<
      SubmitClaimResponse,
      {
        claimId: string;
        submitClaimRequest: SubmitClaimRequest;
        idempotencyKey: string;
      }
    >({
      query: ({ claimId, submitClaimRequest, idempotencyKey }) => ({
        url: `/v2/provider/claims/${claimId}/submit`,
        method: 'POST',
        body: submitClaimRequest,
        headers: { 'Idempotency-Key': idempotencyKey },
      }),
      invalidatesTags: (result, _error, arg) =>
        result
          ? [
              { type: 'Claim', id: arg.claimId },
              { type: 'ClaimsList', id: 'LIST' },
              { type: 'BillingClaimsList', id: 'LIST' },
              { type: 'ClaimQueueCounts', id: 'LIST' },
              { type: 'BillingV2Overview' as const },
              { type: 'BillingV2Work' as const },
              { type: 'BillingV2Claims' as const },
              { type: 'BillingV2ClaimWork' as const },
            ]
          : [],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.claim) {
            dispatch(
              billingApi.util.updateQueryData(
                'getV2ProviderClaimsByClaimId',
                { claimId: arg.claimId },
                () => data.claim,
              ),
            );
          }
        } catch {
          // Error surfaced by the mutation hook
        }
      },
    }),

    // Single claim X12 (837) download
    downloadClaimX12: build.query<
      { blob: Blob; filename: string },
      { claimId: string }
    >({
      query: ({ claimId }) => ({
        url: `/v2/provider/claims/${claimId}/download`,
        responseHandler: (response: Response) =>
          x12DownloadResponseHandler(response, `Claim_${claimId}.837`),
      }),
      keepUnusedDataFor: 0,
    }),

    // Bulk claim X12 (837) download — returns ZIP
    downloadClaimsX12Bulk: build.mutation<
      { blob: Blob; filename: string },
      { claimIds: string[] }
    >({
      query: ({ claimIds }) => ({
        url: '/v2/provider/claims/download',
        method: 'POST',
        body: { claim_ids: claimIds },
        responseHandler: (response: Response) => {
          const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
          return x12DownloadResponseHandler(response, `Claims_${today}.zip`);
        },
      }),
    }),
  }),
});

// Export hooks with user-friendly names
export const {
  // Claims queries and mutations
  useGetV2ProviderClaimsQuery: useGetClaimsQuery,
  useLazyGetV2ProviderClaimsQuery: useLazyGetClaimsQuery,
  usePostV2ProviderClaimsMutation: useCreateClaimMutation,
  useGetV2ProviderClaimsByClaimIdQuery: useGetClaimByIdQuery,
  useLazyGetV2ProviderClaimsByClaimIdQuery: useLazyGetClaimByIdQuery,
  usePutV2ProviderClaimsByClaimIdMutation: useUpdateClaimMutation,
  useDeleteV2ProviderClaimsByClaimIdMutation: useDeleteClaimMutation,

  // PDF generation
  useGetV2ProviderClaimsByClaimIdPdfQuery: useGetClaimPdfQuery,
  useLazyGetV2ProviderClaimsByClaimIdPdfQuery: useLazyGetClaimPdfQuery,
  useLazyGetStediPdfQuery,

  // Claim scrubbing
  usePostV2ProviderClaimsByClaimIdScrubMutation: useClaimScrubMutation,

  // Claim rebuild
  usePostV2ProviderClaimsByClaimIdRebuildMutation: useClaimRebuildMutation,

  // Claim submission — use useSubmitClaimIdempotentMutation in new call sites;
  // this generated name is retained for any legacy imports.
  usePostV2ProviderClaimsByClaimIdSubmitMutation: useClaimSubmitMutation,
  useSubmitClaimIdempotentMutation,

  // Claim resubmit (correction/void)
  usePostV2ProviderClaimsByClaimIdResubmitMutation: useResubmitClaimMutation,

  // Create secondary (COB) claim from an adjudicated primary
  usePostV2ProviderClaimsByClaimIdSecondaryMutation: useCreateSecondaryClaimMutation,

  // COB confirmation preview for the secondary claim
  useGetV2ProviderClaimsByClaimIdSecondaryPreviewQuery: useSecondaryCobPreviewQuery,

  // Claim attention (dismiss/snooze triage)
  usePostV2ProviderClaimsByClaimIdAttentionMutation: useClaimAttentionMutation,

  // Batch submission
  useBatchSubmitClaimsMutation,
  useGetV2ProviderClaimsBatchSubmitByBatchIdQuery: useGetBatchSubmitProgressQuery,

  // Claim X12 (837) downloads
  useLazyDownloadClaimX12Query,
  useDownloadClaimsX12BulkMutation,
} = injectedClaimsApi;

// Export encounter, fee schedule, and new billing claims hooks with user-friendly names
export const {
  // Org-scoped billing claims (new enriched endpoints)
  useGetV2ProviderBillingClaimsQuery: useGetBillingClaimsListQuery,
  useGetV2ProviderBillingClaimsQueueCountsQuery: useGetClaimQueueCountsQuery,
  useGetV2ProviderBillingClaimsByClaimIdQuery: useGetBillingClaimDetailQuery,

  // Billing encounters list
  useGetV2ProviderBillingEncountersQuery: useGetBillingEncountersQuery,
  useLazyGetV2ProviderBillingEncountersQuery: useLazyGetBillingEncountersQuery,

  // Single encounter detail
  useGetV2ProviderBillingEncountersByEncounterIdQuery: useGetEncounterDetailQuery,

  // Single encounter 5S flow
  useGetV2ProviderBillingEncountersByEncounterIdFlowQuery: useGetEncounterFlowQuery,

  // Unified encounter billing read model
  useGetV2ProviderBillingEncountersByEncounterIdUnifiedQuery: useGetUnifiedEncounterBillingQuery,
  useGetV2ProviderBillingEncountersByEncounterIdOverviewQuery: useGetUnifiedEncounterOverviewQuery,
  useGetV2ProviderBillingEncountersByEncounterIdClaimWorkspaceQuery: useGetUnifiedEncounterClaimWorkspaceQuery,
  usePostV2ProviderBillingEncountersByEncounterIdClaimWorkspaceRefreshMutation: useRefreshUnifiedEncounterClaimWorkspaceMutation,
  useGetV2ProviderBillingEncountersByEncounterIdPaymentsQuery: useGetUnifiedEncounterPaymentsWorkspaceQuery,

  // Encounter readiness (lightweight ready-to-bill signals)
  useGetV2ProviderBillingEncountersByEncounterIdReadinessQuery: useGetEncounterReadinessQuery,

  useGetUnifiedEncounterWorklistQuery,
  useLazyGetUnifiedEncounterWorklistQuery,

  // Fee schedule queries and mutations
  useGetV2ProviderBillingFeeSchedulesBaselineQuery: useGetBaselineFeeScheduleQuery,
  usePutV2ProviderBillingFeeSchedulesBaselineMutation: useUpdateBaselineFeeScheduleMutation,
  useDeleteV2ProviderBillingFeeSchedulesBaselineByCodeIdMutation: useDeleteBaselineFeeScheduleMutation,
  useGetPayerExpectedRatesQuery,
  useUpdatePayerExpectedRatesMutation,
  useDeletePayerExpectedRateMutation,

  // Remittances
  useGetV2ProviderBillingRemittancesQuery: useGetProviderRemittancesQuery,
  useGetV2ProviderBillingRemittancesByRemittanceIdQuery: useGetProviderRemittanceDetailQuery,
  useGetV2ProviderBillingRemittancesClaimsByClaimIdSummaryQuery: useGetProviderClaimRemittanceSummaryQuery,
  useGetV2ProviderBillingRemittancesQueueCountsQuery: useGetRemittanceQueueCountsQuery,

  // Fee schedule import from extraction
  usePostV2ProviderBillingFeeSchedulesFromExtractionMutation: useImportFeeScheduleFromExtractionMutation,

  // Org-wide invoice list (local DB)
  useGetV2ProviderBillingInvoicesQuery: useGetBillingInvoicesQuery,

  // Patient balance
  useGetV2ProviderBillingPatientsByPatientIdBalanceQuery: useGetPatientBalanceQuery,

  // Patient payment mutations. `useRecordPaymentMutation` handles both
  // plain payment records and the external-collection flavor (the latter
  // sets create_charge=true + encounter_id; see LogCollectionForm).
  usePostV2ProviderBillingPatientsByPatientIdPaymentsMutation: useRecordPaymentMutation,
  usePostV2ProviderBillingChargesMutation: useCaptureChargesMutation,
  usePostV2ProviderBillingPatientsByPatientIdWriteOffsMutation: useWriteOffMutation,
  usePostV2ProviderBillingChargeLinesByChargeLineIdReverseMutation:
    useReverseChargeLineMutation,

  // CPT charge lookup
  usePostV2ProviderBillingCptChargesLookupMutation: useCptChargesLookupMutation,

  // Statements
  useGetV2ProviderBillingStatementsQueueQuery: useGetStatementsQueueQuery,
  useGetV2ProviderBillingStatementsHistoryQuery: useGetStatementsHistoryQuery,

  // ERA (835) downloads
  useLazyDownloadRemittanceEraQuery,
  useDownloadRemittanceErasBulkMutation,
} = extendedBillingApi;

// Export claims API as billingApi for backward compatibility
export const billingApi = extendedClaimsApi;

// Export billing encounters API for selectors
export const billingEncountersApi = extendedBillingApi;
